'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import type { PaketItem } from '@/lib/paket';
import { PaketItemCard } from './paket-item-card';
import { PaketItemModal } from './paket-item-modal';

interface PaketIncludesProps {
  items: PaketItem[];
}

/**
 * "Detta ingår i paketet" — the grid of the package's contents, and the quick
 * view it opens.
 *
 * Owns which child is open, as an index rather than an object, so the open
 * state cannot drift from the list it points into.
 *
 * Renders nothing on an empty list. That is not an edge case but today's normal
 * path: `th_paket_innehall` is filled on 1 of the 85 paket, so the section has
 * to be absent rather than empty for the page to read as finished.
 */
export function PaketIncludes({ items }: PaketIncludesProps) {
  const t = useTranslations('product');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  if (items.length === 0) return null;

  const open = openIndex == null ? null : items[openIndex];

  return (
    <section className="mt-4">
      <div className="mb-1.5 flex items-baseline justify-between gap-4">
        <h2 className="m-0 font-heading text-[22px] font-semibold text-heading sm:text-[24px]">
          {t('paketIncludes')}
        </h2>
        <span className="shrink-0 text-[12.5px] text-foreground/50">
          {t('productCount', { count: items.length })}
        </span>
      </div>

      <p className="mb-[18px] text-[13px] text-foreground/50">{t('paketIncludesHint')}</p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item, index) => (
          <PaketItemCard key={item.sku} item={item} onOpen={() => setOpenIndex(index)} />
        ))}
      </div>

      {open && <PaketItemModal item={open} onClose={() => setOpenIndex(null)} />}
    </section>
  );
}
